/**
 * MainCoverageCell — Phase 6 Plan 02 Task 3 (D-11 / D-13).
 *
 * The per-rep COVERAGE cell for the Sales-Manager "Team" tab pipeline-by-rep
 * table. Extracts the MainPacingTile coverage chip visual VERBATIM (▲/▼ NN%
 * coverage, good-bg/warn-bg) so the Team tab reads in the same visual family as
 * the Main (MAIN-17) and CRO (CRO-02/03/04) coverage tiles.
 *
 * The route computes `pctOfTarget = openPipeline / (3 × max(0, quota − closedWon))`
 * server-side (team.ts, D-11). This cell only renders it:
 *   - pctOfTarget ≥ 1 → green  `▲ NN% coverage`  (on/ahead of the 3× target)
 *   - pctOfTarget < 1 → amber  `▼ NN% coverage`  (behind the 3× target)
 *   - noQuota === true → the MANDATORY honest "No quota defined" empty state
 *     (em-dash `—` in the numeric slot + an ink-500 note), NEVER a fabricated 3×
 *     target and NEVER a misleading `0%` (D-13 / Pitfall 5). Mirrors MAIN-44's
 *     placeholder_text / confidence:'low' discipline.
 *
 * The chip classes (px-2 py-0.5 text-[11.5px] font-bold) are carried through
 * UNCHANGED from MainPacingTile:229 — py-0.5 (2px) and text-[11.5px] are
 * established sub-4 micro-values (06-UI-SPEC Spacing exceptions), not to be
 * "corrected".
 */

const EM_DASH = '—';

// 06-UI-SPEC Copywriting Contract — "Empty state — no quota (D-13, MANDATORY)".
const NO_QUOTA_HEADING = 'No quota defined';
const NO_QUOTA_BODY =
  'Per-rep coverage needs an AE quota in admin_quotas for this quarter. Ask RevOps to load quotas.';

interface MainCoverageCellProps {
  /** openPipeline / coverageTarget from the route; null when target is 0 or absent. */
  pctOfTarget: number | null;
  /** True when the rep has no admin_quotas row for the current quarter (D-13). */
  noQuota: boolean;
}

export function MainCoverageCell({
  pctOfTarget,
  noQuota,
}: MainCoverageCellProps): React.JSX.Element {
  // MANDATORY honest empty state (D-13 / Pitfall 5) — em-dash + note, no chip, no 0%.
  if (noQuota) {
    return (
      <div data-testid="coverage-no-quota">
        <span className="tabular-nums text-ink-900">{EM_DASH}</span>
        <p className="mt-0.5 text-xs text-ink-500" title={NO_QUOTA_BODY}>
          {NO_QUOTA_HEADING}
        </p>
      </div>
    );
  }

  // Quota exists but the 3× remaining target is 0 (fully banked) → honest em-dash,
  // NOT a fabricated % (Rule 2: never mislead when the ratio is undefined).
  if (pctOfTarget === null) {
    return (
      <span className="tabular-nums text-ink-900" data-testid="coverage-none">
        {EM_DASH}
      </span>
    );
  }

  const pct = Math.round(pctOfTarget * 100);
  const onTrack = pctOfTarget >= 1;

  return (
    <div>
      <span
        data-testid="coverage-chip"
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-bold tabular-nums ${
          onTrack ? 'bg-good-bg text-good-text' : 'bg-warn-bg text-warn-text'
        }`}
      >
        <span aria-hidden>{onTrack ? '▲' : '▼'}</span> {pct}% coverage
      </span>
      {/* Context sub-line — mirror MainPacingTile:243 "coverage vs 3× quota". */}
      <p className="mt-0.5 text-xs text-ink-500">open pipeline vs 3× remaining quota</p>
    </div>
  );
}
